import { Message } from '../types/chat';
import { AuthService } from './auth';

export interface UploadedImage {
  key: string;
  url: string;
  uploaded: string;
  size: number;
}

export interface ListImagesResponse {
  images: UploadedImage[];
  cursor: string | null;
  hasMore: boolean;
}

export class ApiService {
  private baseUrl = '/api';
  constructor(private auth: AuthService) {}

  private async fetchWithAuth(
    url: string,
    init: RequestInit = {},
    retry: boolean = true
  ): Promise<Response> {
    const requestInit = this.auth.applyAuthHeader({ ...init });
    const response = await fetch(url, requestInit);

    if (response.status === 401) {
      if (retry) {
        await this.auth.handleUnauthorized();
        return this.fetchWithAuth(url, init, false);
      }
      this.auth.clearAuthKey();
    }

    return response;
  }

  async sendMessage(messages: Message[], model: string): Promise<AsyncIterable<string>> {
    const body = JSON.stringify({ messages, model });
    const response = await this.fetchWithAuth(`${this.baseUrl}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' })) as any;
      throw new Error(error.error || 'Failed to send message');
    }

    return this.streamResponse(response);
  }

  private async *streamResponse(response: Response): AsyncIterable<string> {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) {
            continue;
          }

          const data = trimmed.slice(6); // Remove 'data: ' prefix
          if (data === '[DONE]') {
            continue;
          }

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices[0]?.delta?.content;
            if (content) {
              yield content;
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }

      const leftover = buffer.trim();
      if (leftover && leftover.startsWith('data: ')) {
        const data = leftover.slice(6);
        if (data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices[0]?.delta?.content;
            if (content) {
              yield content;
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  async fetchModels(): Promise<string[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/models`);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized request');
      }
      throw new Error('Failed to fetch models');
    }

    const data = await response.json() as any;
    return data.models.map((m: any) => m.id);
  }

  async listImages(cursor?: string, limit: number = 10): Promise<ListImagesResponse> {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    if (cursor) {
      params.set('cursor', cursor);
    }

    const response = await this.fetchWithAuth(`${this.baseUrl}/list-images?${params.toString()}`);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized request');
      }
      throw new Error('Failed to list images');
    }

    return await response.json() as ListImagesResponse;
  }

  async deleteAllImages(): Promise<{ success: boolean; deletedCount: number; message: string }> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/delete-all-images`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized request');
      }
      throw new Error('Failed to delete images');
    }

    return await response.json() as { success: boolean; deletedCount: number; message: string };
  }
}
